import { useEffect, useState } from "react";
import { Loader2, Play, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  listBackfillRuns,
  runBackfillNow,
  type BackfillRunHistoryRow,
} from "@/lib/composerCore";

const formatDateTime = (iso: string) => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

export const BackfillRunsPanel = () => {
  const [runs, setRuns] = useState<BackfillRunHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await listBackfillRuns(20);
      setRuns(data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load runs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const res = await runBackfillNow();
      if (!res.ok || res.result?.error) {
        toast.error(res.result?.error ?? `Backfill failed (status ${res.status})`);
      } else {
        const r = res.result;
        toast.success(
          `Backfill complete · scanned ${r.scanned_suppressions ?? 0}, updated ${r.dispatches_updated ?? 0} dispatches, appended ${r.events_appended ?? 0} events`,
        );
      }
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to run backfill");
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="text-base">Backfill — late post-send events</CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            Scans the last 90 days of suppressions and appends late bounce/complaint events to
            dispatches that already completed.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void refresh()} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={() => void handleRunNow()} disabled={running}>
            {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            Run now
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : runs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No backfill runs recorded yet.</p>
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border">
            {runs.map((run) => (
              <li key={run.id} className="flex flex-wrap items-start justify-between gap-2 p-3">
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge
                      variant={run.status === "succeeded" ? "secondary" : "destructive"}
                      className="capitalize"
                    >
                      {run.status}
                    </Badge>
                    <Badge variant="outline" className="capitalize">
                      {run.trigger}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {formatDateTime(run.created_at)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Scanned {run.scanned_suppressions} · Updated {run.dispatches_updated} dispatches ·
                    Appended {run.events_appended} events
                    {run.error_count > 0 ? ` · ${run.error_count} errors` : ""}
                  </p>
                  {run.error_message ? (
                    <p className="break-all text-xs text-destructive">{run.error_message}</p>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};


